import {useAnalytics} from '@/hooks/useAnalytics';
import {useIsMobile} from '@/hooks/useIsMobile';
import {useCallback, useMemo} from 'react';
import type {PopupType} from '@/hooks/usePopupManager';

/** How the user closed the popup. */
type CloseMethod = 'x-button' | 'backdrop' | 'decline-link' | 'auto-dismiss' | 'swipe';

interface PopupAnalyticsContext {
  /** Page scroll depth (0–100) at the time the popup triggered. */
  scrollDepth?: number;
  /** Number of event listings viewed in this session. */
  eventsViewed?: number;
  /** Whether this is the visitor's first ever session. */
  isFirstSession?: boolean;
}

interface UsePopupAnalyticsReturn {
  /** Fire when the popup becomes visible to the user. */
  trackImpression: () => void;
  /** Fire when the user focuses the email input. */
  trackFormFocus: () => void;
  /** Fire when the form is submitted successfully. */
  trackSubmission: () => void;
  /** Fire when the popup is closed, recording how the user dismissed it. */
  trackClose: (method: CloseMethod) => void;
}

/**
 * Provides structured Mixpanel event tracking for popup lifecycle events:
 * impressions, form interactions, submissions, and close actions.
 *
 * Base properties (popup_type, device_type, page_url, is_first_session,
 * scroll_depth, events_viewed) are shared across all events.
 */
function usePopupAnalytics(
  popupType: PopupType,
  context: PopupAnalyticsContext = {},
): UsePopupAnalyticsReturn {
  const {track} = useAnalytics();
  const isMobile = useIsMobile();

  const {scrollDepth, eventsViewed, isFirstSession} = context;

  const baseProps = useMemo(
    () => ({
      popup_type: popupType,
      device_type: isMobile ? 'mobile' : 'desktop',
      is_first_session: isFirstSession ?? false,
      scroll_depth: scrollDepth,
      events_viewed: eventsViewed,
    }),
    [popupType, isMobile, isFirstSession, scrollDepth, eventsViewed],
  );

  /**
   * Merges base props with the current page URL, read at call-time so SPA
   * navigation doesn't leave a stale url in analytics events.
   */
  const getBaseProps = useCallback(
    () => ({
      ...baseProps,
      page_url: typeof window !== 'undefined' ? window.location.href : '',
    }),
    [baseProps],
  );

  const trackImpression = useCallback(() => {
    track('Popup Impression', getBaseProps());
  }, [track, getBaseProps]);

  const trackFormFocus = useCallback(() => {
    track('Popup Form Focus', getBaseProps());
  }, [track, getBaseProps]);

  const trackSubmission = useCallback(() => {
    track('Popup Submission', getBaseProps());
  }, [track, getBaseProps]);

  const trackClose = useCallback(
    (method: CloseMethod) => {
      track('Popup Closed', {...getBaseProps(), close_method: method});
    },
    [track, getBaseProps],
  );

  return {trackImpression, trackFormFocus, trackSubmission, trackClose};
}

export {usePopupAnalytics};
export type {CloseMethod};
